/**
 * Pied de page de la vitrine du produit.
 *
 * ★ **Il ne porte plus le nom d'un théâtre, et plus les deux portes**
 * (`/portail`, `/admin`). Cette page ne vit que sur l'apex du domaine, où
 * aucun théâtre n'est desservi : ces deux liens y menaient à des écrans qui ne
 * peuvent rien charger.
 *
 * Ils sont remplacés par la seule phrase utile à quelqu'un qui atterrit ici
 * alors qu'il cherchait sa propre salle : **votre théâtre a sa propre
 * adresse**. C'est là que se trouvent sa billetterie, son espace abonné et sa
 * connexion du personnel.
 */

import { Link } from 'react-router'

import { useTraduireVitrine } from '../../lib/langue'

import { ancre, ID_CONTACT, ID_TARIFS, lienAideVitrine } from './ancres'
import { EMAIL_ILYGO, RAISON_SOCIALE_ILYGO } from './editeur'
import { Conteneur } from './elements'
import { estSiteInternational } from './marche'
import { CHEMIN_ESSAI } from './publics'
import { ENTETE, interpoler, PIED } from './textes'

export function PiedVitrine() {
  const t = useTraduireVitrine()

  return (
    /* ★ Le pied ferme la page sur le noir de salle, comme le hero l'ouvre :
         c'est ce qui fait tenir les dix-sept sections entre deux bornes plutôt
         que de les laisser se déliter dans du blanc. */
    <footer className="border-t border-white/10 bg-nuit-950 pt-12 pb-14 text-slate-400">
      <Conteneur className="grid gap-6 text-sm">
        <p className="max-w-[62ch]">
          <strong className="font-semibold text-white">
            {t(PIED.chercheSaBilletterieTitre)}
          </strong>{' '}
          {t(PIED.chercheSaBilletterieTexte)}
        </p>

        {/* ★ LA MENTION DE TVA, UNE FOIS ET UNE SEULE (22/08).

            Elle vivait dans le chapeau de la section 12, au-dessus d'une grille
            de trois montants, et nulle part ailleurs — alors que la page publie
            aussi quatre paliers d'événement et deux prestations d'installation.
            Le client a tranché : « soit il faut le mettre PARTOUT où on parle de
            montant, soit NULLE PART. Je serais pour mettre une petite remarque
            AU FOND DE LA PAGE. »

            ⚠️ La règle qui va avec, pour la prochaine personne qui ajoutera un
            prix quelque part : ne pas lui accoler « HT ». Un seul endroit porte
            la mention, et il couvre tous les montants de la page. */}
        <p className="max-w-[62ch]">
          {estSiteInternational()
            ? 'Tous les prix en EUR affichés sur cette page sont indicatifs et hors taxes (HT). Le contrat et la facturation sont en CHF ; le montant converti peut évoluer selon le cours de change.'
            : t(PIED.tva)}
        </p>

        <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3.5 border-t border-white/10 pt-6">
          <p>
            {interpoler(t(PIED.editeurLigne), { editeur: RAISON_SOCIALE_ILYGO })}
          </p>
          <nav aria-label={t(PIED.liensPied)} className="flex flex-wrap gap-x-5 gap-y-1.5">
            <a
              href={lienAideVitrine()}
              className="text-slate-300 no-underline hover:text-white hover:underline"
            >
              {t(ENTETE.documentation)}
            </a>
            <a
              href={ancre(ID_TARIFS)}
              className="text-slate-300 no-underline hover:text-white hover:underline"
            >
              {t(ENTETE.tarif)}
            </a>
            <a
              href={ancre(ID_CONTACT)}
              className="text-slate-300 no-underline hover:text-white hover:underline"
            >
              {t(ENTETE.nousEcrire)}
            </a>
            <Link
              to={CHEMIN_ESSAI}
              className="text-slate-300 no-underline hover:text-white hover:underline"
            >
              {t(ENTETE.essai)}
            </Link>
            <a
              href={`mailto:${EMAIL_ILYGO}`}
              className="text-slate-300 no-underline hover:text-white hover:underline"
            >
              {EMAIL_ILYGO}
            </a>
            <a href="/confidentialite-vitrine.html" className="text-slate-300 no-underline hover:text-white hover:underline">
              {t({ fr: 'Confidentialité de la vitrine', de: 'Datenschutz der Produktseite', en: 'Marketing page privacy' })}
            </a>
          </nav>
        </div>
      </Conteneur>
    </footer>
  )
}
